/**
 * Solicitacoes (spec 007, secao 7): a fila com abas e filtro por tipo, e a decisao de cada pedido.
 * LIBERACAO: etapa inicial e plataforma; a conta declarada (mesa da etapa e a subconta que a extranet gerou), conferida
 * num dialogo antes de aprovar (spec 007, decisao 21: a Prop nao consulta a Nelogica); ou recusar.
 * AVANCO_ETAPA: plataforma (Black saindo do Teste), mensalidade antecipada quando ha portao, a mesa da etapa alvo (e a
 * subconta nova rumo a Mesa Real); comprovante e confirmacao; revogar (ADMIN). As recusas do backend aparecem como vieram.
 * APROVACAO_DO_CHALLENGE (Global, spec 064, modulo `nelogica-v2`): mora em AprovacaoDoChallenge.tsx, com a aba
 * "Funded a liberar". A aba Avancos e da Prop (modulo `etapas`). Cada aba aparece pelo modulo, nunca pela marca.
 */
import {
  Abas, Aviso, Botao, BotaoLink, CabecalhoDePagina, Campo, CampoCaixa, CampoData, CampoDinheiro, CampoSelecao, CampoTextoLongo, Carregando, Dialogo, Fatos, IconeVoltar, LinhaDoTempo,
  LinkInterno, Pilha, Secao, Segmentado, Selo, Tabela, Vazio, diaEmSaoPaulo, formatarDataHora, formatarDia, formatarReais, hojeEmSaoPaulo, type Fato,
} from "@mtc/ui";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import {
  ETAPA, FAMILIA, MODULO_DO_TIPO, PLATAFORMA, STATUS_DA_SOLICITACAO, TIPOS_DE_SOLICITACAO, erroDe, nomeDaAcao, quem, resumoDe, useAcao, useApi, usePapel,
  type Auditoria, type AvancoALiberar, type DetalheDaMatricula, type Etapa, type ItemDaFila, type Plataforma, type SolicitacaoPainel, type StatusSolicitacao,
} from "../api";
import { useAmbiente } from "../contexto";
import { SubcontaDoPlano } from "../subconta";
import { ComprovanteDoAlvo } from "./Comprovantes";
import { PedidoDeRepasse, type DadosDoPedidoDeRepasse } from "./Repasses";
import { ConfirmarConta, DeclararConta, parDaConta, useContaEmUso, useContasConhecidas, type ContaDeclarada, type CorpoDaConfirmacao } from "./DeclararConta";
import { DecisaoDaAprovacao, FilaDeFunded, useFundedALiberar } from "./AprovacaoDoChallenge";
import { ContaNaCorretoraDaMatricula } from "./ContasNaCorretora";
import { BeneficiosDaMatricula, type ModuloBeneficios } from "./Beneficios";
import type { ModuloContaNaCorretora, ModuloSaldo } from "../api";

type AbaFila = "pendentes" | "pagamento" | "avancos" | "funded" | "decididas";
const DECIDIDAS: StatusSolicitacao[] = ["APROVADA", "RECUSADA", "REVOGADA", "CANCELADA"];

function useLista(status: StatusSolicitacao, tipo: string, ligado = true) {
  const { ler } = useApi();
  return useQuery({ queryKey: ["solicitacoes", status, tipo], queryFn: () => ler<{ itens: SolicitacaoPainel[] }>(`/v1/admin/solicitacoes?status=${status}&limite=100${tipo ? `&tipo=${tipo}` : ""}`), enabled: ligado });
}

// ---------------------------------------------------------------- fila

export function PaginaSolicitacoes() {
  const [params, setParams] = useSearchParams();
  const navegar = useNavigate();
  const { ler } = useApi();
  const { modulos } = useAmbiente();
  // Avancos a liberar sao da Prop (etapas, spec 015); Funded a liberar, da Global (nelogica-v2, spec 064).
  const comAvancos = modulos.includes("etapas");
  const comFunded = modulos.includes("nelogica-v2");
  const abasValidas: AbaFila[] = ["pendentes", "pagamento", ...(comAvancos ? ["avancos" as const] : []), ...(comFunded ? ["funded" as const] : []), "decididas"];
  const aba = (abasValidas.includes(params.get("aba") as AbaFila) ? params.get("aba") : "pendentes") as AbaFila;
  const tipo = params.get("tipo") ?? "";
  const mudar = (chave: string, valor: string) => { const p = new URLSearchParams(params); if (valor) p.set(chave, valor); else p.delete(chave); setParams(p, { replace: true }); };
  // Spec 059 (Vitor, 23/10): achar na fila pelo nome, pelo CPF ou pela subconta do plano. Filtra o que a aba ja trouxe.
  const [busca, setBusca] = useState("");
  const termo = busca.trim().toLowerCase();
  const digitos = termo.replace(/\D/g, "");
  const bate = (s: SolicitacaoPainel) => !termo || s.pessoa.nome.toLowerCase().includes(termo) || (digitos.length >= 3 && (s.pessoa.cpf.replace(/\D/g, "").includes(digitos) || (s.subconta ?? "").includes(digitos)));
  const pendentes = useLista("PENDENTE", tipo);
  const pagamento = useLista("AGUARDANDO_PAGAMENTO", tipo);
  // Avancos aprovados esperando a conta na extranet (spec 015, decisao 1): fila propria, nao e status de solicitacao.
  const avancos = useQuery({ queryKey: ["avancos"], queryFn: () => ler<{ itens: AvancoALiberar[] }>("/v1/admin/avancos"), enabled: comAvancos && (aba === "avancos" || aba === "pendentes") });
  const funded = useFundedALiberar(comFunded);
  const decididas = useQueries({ queries: DECIDIDAS.map((st) => ({ queryKey: ["solicitacoes", st, tipo], queryFn: () => ler<{ itens: SolicitacaoPainel[] }>(`/v1/admin/solicitacoes?status=${st}&limite=30${tipo ? `&tipo=${tipo}` : ""}`), enabled: aba === "decididas" })) });
  const linhas: SolicitacaoPainel[] = (
    aba === "pendentes" ? pendentes.data?.itens ?? [] : aba === "pagamento" ? pagamento.data?.itens ?? [] : decididas.flatMap((d) => d.data?.itens ?? []).sort((a, b) => (b.decidida_em ?? b.criada_em).localeCompare(a.decidida_em ?? a.criada_em))
  ).filter(bate);
  const carregando = aba === "pendentes" ? pendentes.isLoading : aba === "pagamento" ? pagamento.isLoading : decididas.some((d) => d.isLoading);
  const erro = aba === "pendentes" ? pendentes.isError : aba === "pagamento" ? pagamento.isError : decididas.some((d) => d.isError);
  return (
    <Pilha espaco={5}>
      <CabecalhoDePagina titulo="Solicitações" descricao={comAvancos ? "Liberações da plataforma e pedidos de análise. Abra um pedido pra decidir." : comFunded ? "Pedidos de aprovação do Challenge e a liberação da conta Funded. Abra um pedido pra decidir." : "Pedidos dos clientes. Abra um pedido pra decidir."} />
      <div className="mtc-linha mtc-linha--entre" style={{ gap: "var(--e-4)", alignItems: "flex-end" }}>
        <div style={{ flex: "1 1 420px", minWidth: 0 }}>
          <Abas
            rotulo="Situação"
            valor={aba}
            aoMudar={(v) => mudar("aba", v === "pendentes" ? "" : v)}
            abas={[
              { valor: "pendentes", rotulo: "Pendentes", contagem: pendentes.data?.itens.length },
              { valor: "pagamento", rotulo: "Aguardando pagamento", contagem: pagamento.data?.itens.length },
              ...(comAvancos ? [{ valor: "avancos" as const, rotulo: "Avanços", contagem: avancos.data?.itens.length }] : []),
              ...(comFunded ? [{ valor: "funded" as const, rotulo: "Funded a liberar", contagem: funded.data?.itens.length }] : []),
              ...(aba === "decididas" ? [{ valor: "decididas", rotulo: "Decididas" }] : []),
            ]}
          />
        </div>
        {aba !== "avancos" && aba !== "funded" && (
          <div style={{ flex: "0 1 260px", minWidth: 200 }}>
            <Campo rotulo="Buscar" valor={busca} aoMudar={setBusca} placeholder={modulos.includes("nelogica-v1") || !comFunded ? "Nome, CPF ou subconta" : "Nome, CPF ou conta"} />
          </div>
        )}
        {aba !== "avancos" && aba !== "funded" && (
          <div style={{ flex: "0 1 260px", minWidth: 200 }}>
            <CampoSelecao rotulo="Tipo" valor={tipo} aoMudar={(v) => mudar("tipo", v)} vazio="Todos os tipos" opcoes={Object.entries(TIPOS_DE_SOLICITACAO).filter(([valor]) => !MODULO_DO_TIPO[valor] || modulos.includes(MODULO_DO_TIPO[valor]!)).map(([valor, rotulo]) => ({ valor, rotulo }))} />
          </div>
        )}
        {aba !== "decididas" && <BotaoLink href="/solicitacoes?aba=decididas" tamanho="pequeno" variante="fantasma">Ver decididas</BotaoLink>}
      </div>
      {erro && <Aviso tipo="erro">{erroDe(aba === "pendentes" ? pendentes.error : aba === "pagamento" ? pagamento.error : decididas.find((d) => d.error)?.error)}</Aviso>}
      {aba === "avancos" ? (
        <FilaDeAvancos />
      ) : aba === "funded" ? (
        <FilaDeFunded />
      ) : carregando ? (
        <Carregando compacto texto="Buscando a fila" />
      ) : (
        <Tabela
          linhas={linhas}
          chave={(s) => s.id}
          aoClicar={(s) => navegar(`/solicitacoes/${s.id}`)}
          vazio={termo ? <Vazio titulo="Nada com essa busca">Confira o nome, o CPF ou a {comAvancos || !comFunded ? "subconta" : "conta"}.</Vazio> : <Vazio titulo="Fila vazia">{aba === "pendentes" ? "Nenhum pedido esperando decisão." : aba === "pagamento" ? (comAvancos || !comFunded ? "Nenhum avanço esperando pagamento." : "Nenhuma taxa de ativação esperando pagamento.") : "Nenhum pedido decidido ainda."}</Vazio>}
          colunas={[
            { chave: "cliente", rotulo: "Cliente", celula: (s) => <><LinkInterno href={`/solicitacoes/${s.id}`}>{s.pessoa.nome}</LinkInterno><span className="mtc-tabela__apoio mtc-mono">{s.pessoa.cpf}</span></> },
            { chave: "plano", rotulo: "Plano", celula: (s) => <>{s.matricula.plano.nome}<SubcontaDoPlano subconta={s.subconta} /></> },
            { chave: "pedido", rotulo: "Pedido", celula: (s) => <>{s.titulo}<span className="mtc-tabela__apoio">{s.resumo}</span></> },
            { chave: "status", rotulo: "Status", celula: (s) => <Selo tom={STATUS_DA_SOLICITACAO[s.status].tom}>{STATUS_DA_SOLICITACAO[s.status].rotulo}</Selo> },
            aba === "decididas"
              ? { chave: "quando", rotulo: "Decidido em", celula: (s) => <span className="mtc-mono">{s.decidida_em ? formatarDataHora(s.decidida_em) : ""}</span> }
              : { chave: "quando", rotulo: "Pedido em", celula: (s) => <span className="mtc-mono">{formatarDataHora(s.criada_em)}</span> },
          ]}
        />
      )}
    </Pilha>
  );
}


// ---------------------------------------------------------------- avancos a liberar (spec 015)

/**
 * "vence hoje as 14h", "vence amanha", "atrasado ha 1 dia". A conta e a mesma que escreve a promessa no e-mail. Serve a
 * fila de avancos (Prop) e a de Funded a liberar (Global): as duas mandam o prazo, o vencimento e o atraso.
 */
export function prazoEmPalavras(a: Pick<AvancoALiberar, "vence_em" | "prazo" | "atrasado">): { texto: string; tom: "neutro" | "alerta" | "erro" } {
  if (!a.vence_em) return { texto: a.prazo ?? "sem prazo", tom: "neutro" };
  const vence = new Date(a.vence_em);
  const horas = Math.round((vence.getTime() - Date.now()) / 3_600_000);
  if (a.atrasado) return { texto: horas > -24 ? "atrasado" : `atrasado há ${Math.floor(-horas / 24)} dia${horas <= -48 ? "s" : ""}`, tom: "erro" };
  if (horas <= 12) return { texto: `vence ${diaEmSaoPaulo(vence) === hojeEmSaoPaulo() ? "" : "amanhã "}às ${vence.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" })}`, tom: "alerta" };
  return { texto: `vence ${formatarDataHora(a.vence_em)}`, tom: "neutro" };
}

function FilaDeAvancos() {
  const { ler } = useApi();
  const [abrindo, setAbrindo] = useState<AvancoALiberar>();
  const [feito, setFeito] = useState<string>();
  const q = useQuery({ queryKey: ["avancos"], queryFn: () => ler<{ itens: AvancoALiberar[] }>("/v1/admin/avancos") });
  if (q.isLoading) return <Carregando compacto texto="Buscando os avanços" />;
  if (q.isError) return <Aviso tipo="erro">{erroDe(q.error)}</Aviso>;
  const itens = q.data?.itens ?? [];
  return (
    <Pilha espaco={4}>
      {feito && <Aviso tipo="sucesso">{feito}</Aviso>}
      <Tabela
        linhas={itens}
        chave={(a) => a.solicitacao_id}
        vazio={<Vazio titulo="Nada a liberar">Todo avanço aprovado já teve a conta registrada.</Vazio>}
        colunas={[
          { chave: "trader", rotulo: "Cliente", celula: (a) => <><LinkInterno href={`/matriculas/${a.matricula_id}`}>{a.trader}</LinkInterno><span className="mtc-tabela__apoio mtc-mono">{a.cpf}</span><span className="mtc-tabela__apoio">{a.plano}</span></> },
          { chave: "etapa", rotulo: "Avanço", celula: (a) => <>{a.de_rotulo} <span aria-hidden="true">→</span> {a.para_rotulo}<span className="mtc-tabela__apoio">{a.plataforma ? PLATAFORMA[a.plataforma] : ""}</span></> },
          { chave: "fazer", rotulo: "Na extranet", celula: (a) => (a.o_que_fazer === "criar" ? "Criar a conta da Mesa Real" : `Mover a conta pra mesa do ${a.para_rotulo}`) },
          { chave: "aprovado", rotulo: "Aprovado em", celula: (a) => <span className="mtc-mono">{formatarDataHora(a.aprovada_em)}</span> },
          { chave: "prazo", rotulo: "Prazo", celula: (a) => { const p = prazoEmPalavras(a); return <Selo tom={p.tom === "erro" ? "erro" : p.tom === "alerta" ? "alerta" : "neutro"}>{p.texto}</Selo>; } },
          { chave: "acao", rotulo: "", celula: (a) => <Botao tamanho="pequeno" onClick={() => setAbrindo(a)}>Registrar a liberação</Botao> },
        ]}
      />
      {abrindo && <RegistrarLiberacao a={abrindo} aoFechar={() => setAbrindo(undefined)} aoPronto={(t) => { setAbrindo(undefined); setFeito(t); }} />}
    </Pilha>
  );
}

/** A conta declarada de sempre (007, decisao 7) mais o dia em que ela foi liberada: hoje por padrao, ontem se atrasou. */
function RegistrarLiberacao({ a, aoFechar, aoPronto }: { a: AvancoALiberar; aoFechar: () => void; aoPronto: (texto: string) => void }) {
  const { postar } = useApi();
  const acao = useAcao();
  const qc = useQueryClient();
  const [conta, setConta] = useState<ContaDeclarada>({ subconta: "", mesa: "" });
  const [dia, setDia] = useState(hojeEmSaoPaulo());
  const [conferir, setConferir] = useState(false);
  const mesas = useContasConhecidas(a.matricula_id, a.para);
  const pedeSubconta = a.o_que_fazer === "criar";
  const pronta = Boolean(conta.mesa && (!pedeSubconta || conta.subconta) && dia);
  // Subconta ja com outro plano em andamento na mesma mesa: o backend pergunta e a tela confirma (Vitor, 01/10).
  const emUso = useContaEmUso(parDaConta(conta));
  const pedido = (extra: CorpoDaConfirmacao) => postar(`/v1/admin/solicitacoes/${a.solicitacao_id}/liberacao`, { ...corpoDaConta(conta), dia, ...extra });
  const depois = async (gravou: boolean) => {
    if (!gravou) return;
    await qc.invalidateQueries();
    aoPronto(`${a.trader} está agora em ${a.para_rotulo}. O cliente foi avisado.`);
  };
  const rodape = (
    <>
      <Botao variante="fantasma" onClick={aoFechar}>Cancelar</Botao>
      <Botao disabled={!pronta} onClick={() => { acao.limpar(); setConferir(true); }}>Conferir e registrar</Botao>
    </>
  );
  return (
    <>
      <Dialogo aberto={!conferir} aoFechar={aoFechar} titulo="Registrar a liberação" descricao={`${a.trader}: ${a.de_rotulo} para ${a.para_rotulo}. A etapa muda quando você registrar, e é este dia que vale como início.`} rodape={rodape}>
        <div className="mtc-pilha" style={{ gap: "var(--e-4)" }}>
          {acao.erro && <Aviso tipo="erro">{acao.erro}</Aviso>}
          <DeclararConta matriculaId={a.matricula_id} etapa={a.para} modo={pedeSubconta ? "nova" : "mover"} valor={conta} aoMudar={setConta} />
          <CampoData rotulo="Liberada em" valor={dia} aoMudar={setDia} dica="Hoje, ou o dia em que você moveu a conta na extranet. Não aceita data futura." />
        </div>
      </Dialogo>
      <ConfirmarConta
        aberto={conferir}
        aoFechar={() => setConferir(false)}
        aoConfirmar={() => void emUso.enviar(acao.rodar, pedido).then(depois)}
        emUso={emUso.frase}
        aoConfirmarEmUso={() => void emUso.confirmarEReenviar(acao.rodar, pedido).then(depois)}
        carregando={acao.carregando}
        erro={acao.erro}
        titulo="Confere antes de registrar"
        botao="Registrar a liberação"
        cpf={a.cpf}
        etapa={a.para}
        conta={conta}
        mesas={mesas.data?.mesas ?? []}
        extra={[{ rotulo: "Liberada em", valor: formatarDia(dia) }]}
      />
    </>
  );
}

// ---------------------------------------------------------------- detalhe

export function PaginaSolicitacao() {
  const { id = "" } = useParams();
  const { ler } = useApi();
  const q = useQuery({ queryKey: ["solicitacao", id], queryFn: () => ler<{ solicitacao: SolicitacaoPainel; auditoria: Auditoria[] }>(`/v1/admin/solicitacoes/${id}`) });
  const s = q.data?.solicitacao;
  const mat = useQuery({ queryKey: ["matricula", s?.matricula.id], queryFn: () => ler<DetalheDaMatricula>(`/v1/admin/matriculas/${s!.matricula.id}`), enabled: Boolean(s) });
  const [feito, setFeito] = useState<string>();
  const { modulos } = useAmbiente();
  const contaDaGlobal = modulos.includes("nelogica-v2");
  useEffect(() => setFeito(undefined), [id]);
  if (q.isLoading) return <Carregando compacto texto="Abrindo o pedido" />;
  if (!s) return <Pilha espaco={4}><Voltar /><Aviso tipo="erro">{q.error ? erroDe(q.error) : "Pedido não encontrado."}</Aviso></Pilha>;
  const st = STATUS_DA_SOLICITACAO[s.status];
  const decisao: ReactNode =
    s.tipo === "LIBERACAO" && s.status === "PENDENTE" ? <DecidirLiberacao s={s} aoDecidir={setFeito} />
    : s.tipo === "AVANCO_ETAPA" && s.status === "PENDENTE" ? <DecidirAvanco s={s} m={mat.data} aoDecidir={setFeito} />
    : s.tipo === "AVANCO_ETAPA" && s.status === "AGUARDANDO_PAGAMENTO" ? <ConfirmarPagamento s={s} m={mat.data} aoDecidir={setFeito} />
    : s.tipo === "AVANCO_ETAPA" && s.status === "APROVADA" ? <Revogar s={s} aoDecidir={setFeito} />
    // O pedido de repasse (spec 048): perfil de risco, calculo, espera do cliente e "Pagamento feito", em todos os passos.
    : s.tipo === "PEDIDO_DE_REPASSE" ? <PedidoDeRepasse id={s.id} status={s.status} dados={s.dados as unknown as DadosDoPedidoDeRepasse} subconta={mat.data?.modulos.corretora?.subconta ?? null} aoMudar={setFeito} />
    // Global (spec 064): analise, taxa de ativacao e a liberacao da conta Funded.
    : s.tipo === "APROVACAO_DO_CHALLENGE" ? <DecisaoDaAprovacao s={s} m={mat.data} aoDecidir={setFeito} />
    : null;
  const ehAprovacao = s.tipo === "APROVACAO_DO_CHALLENGE";
  return (
    <Pilha espaco={6}>
      <CabecalhoDePagina
        voltar={<Voltar />}
        titulo={s.pessoa.nome}
        descricao={<>{s.titulo}: {s.matricula.plano.nome}. CPF <span className="mtc-mono">{s.pessoa.cpf}</span>. {s.resumo}.</>}
        acoes={<><Selo tom={st.tom}>{st.rotulo}</Selo><BotaoLink href={`/matriculas/${s.matricula.id}`} tamanho="pequeno">Ver matrícula</BotaoLink></>}
      />
      {feito && <Aviso tipo="sucesso">{feito}</Aviso>}
      <div className="mtc-grade" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 420px), 1fr))" }}>
        <Pilha espaco={5}>
          {decisao}
          <Secao rotulo="Pedido" titulo="O que foi pedido">
            <Fatos
              itens={[
                { rotulo: "Tipo", valor: s.titulo },
                { rotulo: "Pedido em", valor: formatarDataHora(s.criada_em) },
                ...(s.valor_centavos != null ? [{ rotulo: ehAprovacao ? "Taxa de ativação" : "Mensalidade antecipada", valor: formatarReais(s.valor_centavos), mono: true, detalhe: s.pagamento ? `Paga ${s.pagamento.como === "PLATAFORMA" ? "pela plataforma" : "por fora"} em ${formatarDataHora(s.pagamento.pago_em)}` : undefined }] : []),
                ...(s.decidida_em ? [{ rotulo: "Decidido em", valor: formatarDataHora(s.decidida_em), detalhe: quem(s.decidida_por) }] : []),
                ...(s.dados.subconta ? [{ rotulo: "Subconta", valor: String(s.dados.subconta), mono: true }] : []),
                ...(s.dados.mesa ? [{ rotulo: "Mesa", valor: String(s.dados.mesa), mono: true }] : []),
              ]}
            />
            {s.mensagem && <p style={{ margin: 0 }}><span className="mtc-rotulo">Mensagem do cliente</span><br />{s.mensagem}</p>}
            {(() => {
              // Pedido aberto pela equipe no lugar do cliente (spec 013, item 7): quem e por que.
              const pela = (s.dados as { aberta_pela_equipe?: { nome?: string; motivo?: string } } | null)?.aberta_pela_equipe;
              return pela ? <Aviso tipo="info">Aberto pela equipe ({pela.nome}): {pela.motivo}</Aviso> : null;
            })()}
            {s.observacao && <Aviso tipo="alerta">{ehAprovacao ? "Motivo enviado ao cliente" : "Observação enviada ao cliente"}: {s.observacao}</Aviso>}
            {s.valor_centavos != null && s.status !== "AGUARDANDO_PAGAMENTO" && <ComprovanteDoAlvo pessoaId={s.pessoa.id} tipo="PEDIDO" id={s.id} />}
          </Secao>
        </Pilha>
        <Pilha espaco={5}>
          {/* Na Global a matricula e a conta na corretora (spec 043): conta, etapa, estado e saldo, pra julgar o Challenge. */}
          {contaDaGlobal && mat.data ? (
            <ContaNaCorretoraDaMatricula corretora={mat.data.modulos.corretora as unknown as ModuloContaNaCorretora | undefined} saldo={mat.data.modulos.saldo as ModuloSaldo | undefined} />
          ) : (
            <Secao rotulo="Matrícula" titulo="Como está agora">
              {mat.isLoading ? <Carregando compacto /> : mat.data ? <FatosDaMatricula m={mat.data} /> : <Aviso tipo="erro">{erroDe(mat.error)}</Aviso>}
            </Secao>
          )}
          {/* Os beneficios prometidos na venda, pra decidir o pedido sem abrir a matricula (Quezia, 04/11: "se no avanco de
              etapa mostrar os beneficios da matricula e bom tbm"). O mesmo bloco da tela da matricula. */}
          {mat.data && <BeneficiosDaMatricula matriculaId={s.matricula.id} modulo={mat.data.modulos.beneficios as ModuloBeneficios | undefined} />}
          <Secao rotulo="Histórico" titulo="Trilha do pedido">
            <LinhaDoTempo itens={(q.data?.auditoria ?? []).map((a, i) => ({ chave: `${a.em}-${i}`, titulo: nomeDaAcao(a.acao), detalhe: resumoDe(a.depois), quando: `${formatarDataHora(a.em)}, ${quem(a.ator)}`, tom: a.acao.includes("RECUSADA") || a.acao.includes("REVOGADA") ? "erro" : a.acao.includes("APROVADA") ? "sucesso" : "info" }))} vazio="Sem decisões registradas ainda." />
          </Secao>
        </Pilha>
      </div>
    </Pilha>
  );
}

function Voltar() {
  return <LinkInterno href="/solicitacoes" className="mtc-voltar"><IconeVoltar tamanho={16} />Solicitações</LinkInterno>;
}

/**
 * Os fatos da matricula pelos modulos do ambiente (spec 019, criterio 2): familia, etapa e prazo so com `etapas`;
 * subconta, mesa e plataforma so com `nelogica-v1`. Na Global a conta tem secao propria (spec 043), com o estado e o
 * saldo; aqui ela diria "Sem subconta" de uma conta que existe.
 */
export function FatosDaMatricula({ m }: { m: DetalheDaMatricula }) {
  const { modulos } = useAmbiente();
  const e = m.modulos.etapas;
  const c = m.modulos.corretora;
  const comEtapas = modulos.includes("etapas");
  const comSubconta = modulos.includes("nelogica-v1");
  const prazo = !e ? "Começa na liberação" : e.etapa !== "TESTE" ? "Não se aplica" : e.teste_vencido ? `Vencido (${e.teste_ate ? formatarDia(e.teste_ate) : ""})` : e.teste_sem_prazo ? "Sem prazo" : e.teste_ate ? `Até ${formatarDia(e.teste_ate)}` : "Sem prazo";
  const itens: Fato[] = [
    ...(comEtapas
      ? [
          { rotulo: "Família", valor: e?.familia_rotulo ?? "Definida na liberação" },
          { rotulo: "Etapa", valor: e?.etapa_rotulo ?? "Aguardando liberação", detalhe: e?.proxima_etapa ? `Próxima: ${ETAPA[e.proxima_etapa]}` : e ? "Última etapa" : undefined },
          // O inicio de cada etapa ja percorrida (Eduarda, 17/09): etapa 1, 2 e 3.
          ...(e?.inicios ?? []).map((x) => ({ rotulo: `Início da etapa ${x.numero}`, valor: x.dia ? formatarDia(x.dia) : "Não informado", detalhe: x.rotulo })),
          { rotulo: "Prazo do teste", valor: prazo, detalhe: e?.etapa === "TESTE" && e.dias_restantes_do_teste != null && !e.teste_vencido ? `${e.dias_restantes_do_teste} dias restantes` : e?.prazo_motivo ?? undefined },
        ]
      : []),
    ...(comSubconta
      ? [
          { rotulo: "Subconta", valor: c?.subconta ?? "Sem subconta", mono: Boolean(c?.subconta), detalhe: c?.subconta && c.vinculada_em ? `Declarada${c.declarada_por ? ` por ${c.declarada_por}` : ""} em ${formatarDataHora(c.vinculada_em)}` : undefined },
          { rotulo: "Mesa", valor: c?.mesa ?? "Sem mesa", detalhe: c?.mesa_nome ?? undefined, mono: Boolean(c?.mesa) },
          { rotulo: "Plataforma", valor: c?.plataforma_rotulo ?? "Não informada" },
        ]
      : []),
  ];
  return itens.length ? <Fatos itens={itens} /> : null;
}

function useDepois(aoDecidir: (t: string) => void) {
  const qc = useQueryClient();
  return async (texto: string) => {
    await qc.invalidateQueries();
    aoDecidir(texto);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
}

// ---------------------------------------------------------------- liberacao

function DecidirLiberacao({ s, aoDecidir }: { s: SolicitacaoPainel; aoDecidir: (t: string) => void }) {
  const { ler, postar } = useApi();
  const { pode } = usePapel();
  const depois = useDepois(aoDecidir);
  const acao = useAcao();
  const [conta, setConta] = useState<ContaDeclarada>({ subconta: "", mesa: "" });
  const [conferir, setConferir] = useState(false);
  const [recusar, setRecusar] = useState(false);
  const fila = useQuery({ queryKey: ["fila-liberacao"], queryFn: () => ler<{ hoje: string; itens: ItemDaFila[] }>("/v1/admin/agendamento/fila") });
  const item = fila.data?.itens.find((i) => i.solicitacao_id === s.id);
  const pronta = Boolean(conta.subconta && conta.mesa);
  // Subconta ja com outro plano em andamento na mesma mesa: o backend pergunta e a tela confirma (Vitor, 01/10).
  const emUso = useContaEmUso(parDaConta(conta));
  const pedido = (extra: CorpoDaConfirmacao) => postar(`/v1/admin/solicitacoes/${s.id}/aprovar`, { ...conta, ...extra });
  const aprovado = async (gravou: boolean) => {
    if (!gravou) return;
    setConferir(false);
    await depois(`Liberação aprovada: subconta ${conta.subconta} na mesa ${conta.mesa}. O plano está ativo e o cliente foi avisado.`);
  };
  return (
    <Secao destaque rotulo="Decisão" titulo="Liberar a plataforma">
      {fila.isLoading ? <Carregando compacto /> : !item ? (
        <Aviso tipo="alerta">Este pedido não está na fila de liberação. Recarregue a página.</Aviso>
      ) : (
        <>
          <Fatos
            itens={[
              { rotulo: "Início", valor: item.inicio_formatado, detalhe: item.atraso_dias > 0 ? `${item.atraso_dias} ${item.atraso_dias === 1 ? "dia" : "dias"} de atraso` : item.inicio === fila.data?.hoje ? "Hoje" : undefined },
              { rotulo: "Etapa inicial", valor: item.etapa_inicial_rotulo ?? "Plano sem família" },
              { rotulo: "Plataforma", valor: item.plataforma_rotulo ?? "Sem plataforma" },
            ]}
          />
          {!item.etapa_inicial && <Aviso tipo="erro">O plano {item.plano.nome} está sem família. Defina a família em Planos antes de liberar.</Aviso>}
          <ol className="mtc-passos">
            <li>Na extranet da Nelogica, crie a conta do CPF <strong className="mtc-mono">{item.pessoa.cpf}</strong> na mesa sugerida{item.etapa_inicial_rotulo ? ` (ou outra de ${item.etapa_inicial_rotulo})` : ""}, com {item.plataforma_rotulo ?? "a plataforma do cliente"}.</li>
            <li>Declare aqui a subconta que a extranet gerou e a mesa em que você criou.</li>
            <li>Confira e aprove. O sistema não consulta a Nelogica: o que você declarar vira a conta do cliente.</li>
          </ol>
        </>
      )}
      <form className="mtc-formulario" onSubmit={(ev: FormEvent) => { ev.preventDefault(); acao.limpar(); setConferir(true); }} noValidate>
        {!pode("SUPORTE") && <Aviso tipo="info">Decidir solicitações é do suporte pra cima.</Aviso>}
        {item?.etapa_inicial && <DeclararConta matriculaId={s.matricula.id} etapa={item.etapa_inicial} modo="nova" valor={conta} aoMudar={setConta} />}
        <div className="mtc-linha" style={{ gap: "var(--e-2)" }}>
          <Botao type="submit" disabled={!pronta || !pode("SUPORTE")}>Conferir e liberar</Botao>
          <Botao variante="secundario" onClick={() => setRecusar(true)} disabled={!pode("SUPORTE")}>Recusar</Botao>
        </div>
      </form>
      {item?.etapa_inicial && (
        <ConfirmarConta
          aberto={conferir}
          aoFechar={() => setConferir(false)}
          aoConfirmar={() => void emUso.enviar(acao.rodar, pedido).then(aprovado)}
          emUso={emUso.frase}
          aoConfirmarEmUso={() => void emUso.confirmarEReenviar(acao.rodar, pedido).then(aprovado)}
          carregando={acao.carregando}
          erro={acao.erro}
          titulo="Conferir a conta antes de liberar"
          botao="Confirmar e liberar"
          cpf={item.pessoa.cpf}
          etapa={item.etapa_inicial}
          conta={conta}
          mesas={item.mesas}
          extra={[{ rotulo: "Plataforma", valor: item.plataforma_rotulo ?? "Sem plataforma" }]}
        />
      )}
      <DialogoObservacao s={s} verbo="recusar" aberto={recusar} aoFechar={() => setRecusar(false)} aoConcluir={() => depois("Liberação recusada. O cliente foi avisado e pode escolher outra data.")} />
    </Secao>
  );
}

// ---------------------------------------------------------------- avanco

/**
 * O que a decisao deste avanco pede, como o backend decide (`proximo_avanco` do resumo de etapas, spec 007, 4.4). So vale
 * se o pedido e da etapa em que a matricula esta pra proxima; senao, undefined, e a tela mostra todos os campos (o
 * backend recusa com a mensagem certa se faltar algo).
 */
function regraDoAvanco(s: SolicitacaoPainel, m?: DetalheDaMatricula) {
  const e = m?.modulos.etapas;
  return e?.proximo_avanco && e.etapa === s.dados.de && e.proximo_avanco.para === s.dados.para ? e.proximo_avanco : undefined;
}

/** O corpo da conta: na Mesa Real subconta e mesa; movendo, a mesa (e a subconta so se mudou). */
const corpoDaConta = (c: ContaDeclarada) => ({ mesa: c.mesa || undefined, subconta: c.subconta || undefined });

function DecidirAvanco({ s, m, aoDecidir }: { s: SolicitacaoPainel; m?: DetalheDaMatricula; aoDecidir: (t: string) => void }) {
  const { postar } = useApi();
  const { pode } = usePapel();
  const depois = useDepois(aoDecidir);
  const acao = useAcao();
  const e = m?.modulos.etapas;
  const c = m?.modulos.corretora;
  const de = s.dados.de as Etapa | undefined;
  const para = s.dados.para as Etapa | undefined;
  const regra = regraDoAvanco(s, m);
  const destino = (para ?? "MESA_REAL") as Etapa;
  const [plataforma, setPlataforma] = useState<Plataforma>("PROFIT_ONE");
  const [valor, setValor] = useState(0);
  const [conferir, setConferir] = useState(false);
  const [recusar, setRecusar] = useState(false);
  useEffect(() => { if (c?.plataforma) setPlataforma(c.plataforma); }, [c?.plataforma]);
  // O valor da mensalidade antecipada e o que o checkout da compra combinou pra plataforma (spec 007, M6); o operador confirma.
  const valores = m?.modulos.mensalidade?.valores;
  const [valorMexido, setValorMexido] = useState(false);
  useEffect(() => {
    if (valorMexido) return;
    const doCheckout = plataforma === "PROFIT_PRO" ? valores?.profit_pro : valores?.profit_one;
    if (doCheckout != null) setValor(doCheckout);
  }, [valores?.profit_one, valores?.profit_pro, plataforma, valorMexido]);
  const perguntaPlataforma = regra ? regra.pede_plataforma : true;
  const portao = regra ? regra.portao === "SEMPRE" || (regra.portao === "SE_PRO" && plataforma === "PROFIT_PRO") : true;
  // Sem a regra do backend, pede a subconta (como os outros campos): a tela nao deduz regra; o backend recusa se faltar.
  const mesaReal = regra ? regra.pede_subconta_nova : true;
  const cobra = portao && valor > 0;
  const prazo = regra?.prazo;
  const corpo = () => ({ plataforma: perguntaPlataforma ? plataforma : undefined, valor_centavos: cobra ? valor : undefined });
  const aprovar = async () => {
    const ok = await acao.rodar(() => postar(`/v1/admin/solicitacoes/${s.id}/aprovar`, corpo()));
    if (!ok) return;
    setConferir(false);
    await depois(
      cobra
        ? `Avanço aprovado com mensalidade antecipada de ${formatarReais(valor)}. O cliente foi avisado pra pagar pela área.`
        : `Avanço aprovado. Ele entrou na aba Avanços: a etapa muda quando alguém registrar a liberação${prazo ? `, em até ${prazo}` : ""}.`,
    );
  };
  const enviar = (ev: FormEvent) => {
    ev.preventDefault();
    acao.limpar();
    if (cobra) void aprovar();
    else setConferir(true);
  };
  return (
    <Secao destaque rotulo="Decisão" titulo={de && para ? `De ${ETAPA[de]} para ${ETAPA[para]}` : "Decidir o avanço"} acoes={e ? <Selo>{FAMILIA[e.familia]}</Selo> : undefined}>
      <ol className="mtc-passos">
        <li>Confira o resultado do cliente na plataforma.</li>
        <li>{portao ? "Aprove com o valor da mensalidade antecipada: o cliente paga pela área e só então o avanço entra na fila." : "Aprove. Aqui você promete o avanço, ainda não mexe na extranet."}</li>
        <li>
          O pedido vai pra aba <strong>Avanços</strong>. {mesaReal ? <>Lá você cria a conta nova da Mesa Real do CPF <strong className="mtc-mono">{s.pessoa.cpf}</strong></> : <>Lá você move a conta atual{c?.subconta ? <> (subconta <strong className="mtc-mono">{c.subconta}</strong>)</> : null} pra mesa de {para ? ETAPA[para] : "destino"}</>} e registra a liberação. A etapa muda nessa hora{prazo ? <>, e o cliente já foi avisado de que isso leva até <strong>{prazo}</strong></> : null}.
        </li>
      </ol>
      <form className="mtc-formulario" onSubmit={enviar} noValidate>
        {acao.erro && !conferir && <Aviso tipo="erro">{acao.erro}</Aviso>}
        {!pode("SUPORTE") && <Aviso tipo="info">Decidir solicitações é do suporte pra cima.</Aviso>}
        {perguntaPlataforma && <Segmentado rotulo="Plataforma na nova etapa" nome={`plataforma-${s.id}`} valor={plataforma} aoMudar={setPlataforma} opcoes={[{ valor: "PROFIT_ONE", rotulo: PLATAFORMA.PROFIT_ONE }, { valor: "PROFIT_PRO", rotulo: PLATAFORMA.PROFIT_PRO }]} />}
        {portao && <CampoDinheiro rotulo="Mensalidade antecipada" centavos={valor} aoMudar={(v) => { setValorMexido(true); setValor(v); }} dica={regra ? "Valor combinado no checkout da compra (dá pra mudar na matrícula). O cliente paga pela área, por PIX ou cartão ou pelo PIX de recebimento com o comprovante, e é a primeira mensalidade do ciclo." : "Preencha se esta transição cobra mensalidade antecipada."} />}
        <div className="mtc-linha" style={{ gap: "var(--e-2)" }}>
          <Botao type="submit" carregando={cobra && acao.carregando} disabled={!pode("SUPORTE")}>{cobra ? "Aprovar e pedir pagamento" : "Aprovar o avanço"}</Botao>
          <Botao variante="secundario" onClick={() => setRecusar(true)} disabled={!pode("SUPORTE")}>Recusar</Botao>
        </div>
      </form>
      <Dialogo
        aberto={conferir}
        aoFechar={() => setConferir(false)}
        titulo="Aprovar o avanço"
        descricao="O cliente recebe a promessa com o prazo. A etapa e a conta mudam depois, quando alguém registrar a liberação."
        rodape={<><Botao variante="fantasma" onClick={() => setConferir(false)}>Voltar</Botao><Botao carregando={acao.carregando} onClick={() => void aprovar()}>Aprovar o avanço</Botao></>}
      >
        <div className="mtc-pilha" style={{ gap: "var(--e-3)" }}>
          {acao.erro && <Aviso tipo="erro">{acao.erro}</Aviso>}
          <Fatos
            itens={[
              { rotulo: "Trader", valor: s.pessoa.nome, detalhe: s.pessoa.cpf },
              { rotulo: "Avanço", valor: de && para ? `${ETAPA[de]} para ${ETAPA[para]}` : ETAPA[destino] },
              ...(perguntaPlataforma ? [{ rotulo: "Plataforma", valor: PLATAFORMA[plataforma] }] : []),
              ...(prazo ? [{ rotulo: "Prometido ao cliente", valor: `liberação em até ${prazo}` }] : []),
              { rotulo: "Na extranet", valor: mesaReal ? "Criar a conta da Mesa Real" : `Mover a conta pra mesa de ${para ? ETAPA[para] : "destino"}`, detalhe: "na aba Avanços, depois" },
            ]}
          />
        </div>
      </Dialogo>
      <DialogoObservacao s={s} verbo="recusar" aberto={recusar} aoFechar={() => setRecusar(false)} aoConcluir={() => depois("Pedido de análise recusado. O cliente foi avisado com a sua observação.")} />
    </Secao>
  );
}

function ConfirmarPagamento({ s, m, aoDecidir }: { s: SolicitacaoPainel; m?: DetalheDaMatricula; aoDecidir: (t: string) => void }) {
  const { postar } = useApi();
  const { pode } = usePapel();
  const depois = useDepois(aoDecidir);
  const acao = useAcao();
  const regra = regraDoAvanco(s, m);
  const mesaReal = regra ? regra.pede_subconta_nova : true;
  const prazo = regra?.prazo;
  const destino = ((s.dados.para as Etapa | undefined) ?? "MESA_REAL") as Etapa;
  const [conferir, setConferir] = useState(false);
  const [recusar, setRecusar] = useState(false);
  // Reserva (M8): o comprovante chegou pelo WhatsApp e o operador confirma por fora, com observacao.
  const [porFora, setPorFora] = useState(false);
  const [observacao, setObservacao] = useState("");
  const pago = Boolean(s.pagamento);
  const pronta = pago || (porFora && observacao.trim().length >= 5);
  const confirmar = async () => {
    const corpo = !pago && porFora ? { por_fora: { observacao: observacao.trim() } } : {};
    const ok = await acao.rodar(() => postar(`/v1/admin/solicitacoes/${s.id}/confirmar-pagamento`, corpo));
    if (!ok) return;
    setConferir(false);
    await depois(`Pagamento confirmado. O avanço entrou na aba Avanços: a etapa muda quando alguém registrar a liberação${prazo ? `, em até ${prazo}` : ""}.`);
  };
  const pg = s.pagamento;
  return (
    <Secao destaque rotulo="Decisão" titulo={pago ? "Pago: liberar o avanço" : "Aguardando o pagamento"}>
      <Fatos
        itens={[
          { rotulo: "Valor", valor: s.valor_centavos != null ? formatarReais(s.valor_centavos) : "Sem valor", mono: true },
          { rotulo: "Pagamento", valor: pg ? (pg.como === "PLATAFORMA" ? `Pela plataforma${pg.meio ? ` (${pg.meio === "pix" ? "PIX" : "cartão"})` : ""}` : "Por fora") : "Ainda não pago", detalhe: pg ? `${formatarDataHora(pg.pago_em)}${pg.pedido ? `, pedido ${pg.pedido}` : ""}` : "O cliente paga pela área: por PIX ou cartão confirma sozinho; pelo PIX de recebimento, conferir o comprovante em Comprovantes confirma o pagamento." },
          ...(s.dados.plataforma ? [{ rotulo: "Plataforma na nova etapa", valor: PLATAFORMA[s.dados.plataforma as Plataforma] }] : []),
        ]}
      />
      {pg?.observacao && <Aviso tipo="info">Observação do pagamento por fora: {pg.observacao}</Aviso>}
      {/* Spec 039: o comprovante que o cliente mandou pela area; conferir la ja confirma o pagamento. */}
      <ComprovanteDoAlvo pessoaId={s.pessoa.id} tipo="PEDIDO" id={s.id} aviso />
      <form className="mtc-formulario" onSubmit={(ev: FormEvent) => { ev.preventDefault(); acao.limpar(); setConferir(true); }} noValidate>
        {!pago && (
          <Pilha espaco={3}>
            <CampoCaixa marcado={porFora} aoMudar={setPorFora}>Recebi o comprovante por fora (WhatsApp) e confirmo o pagamento</CampoCaixa>
            {porFora && <CampoTextoLongo rotulo="Observação do pagamento" valor={observacao} aoMudar={setObservacao} maxLength={500} required placeholder="Ex.: comprovante do PIX de R$ 297,00 recebido pelo WhatsApp em 14/09." dica="Fica na trilha do pedido e na mensalidade." />}
          </Pilha>
        )}
        <div className="mtc-linha" style={{ gap: "var(--e-2)" }}>
          <Botao type="submit" disabled={!pode("SUPORTE") || !pronta}>Confirmar o pagamento</Botao>
          <Botao variante="secundario" onClick={() => setRecusar(true)} disabled={!pode("SUPORTE")}>Recusar</Botao>
        </div>
      </form>
      <Dialogo
        aberto={conferir}
        aoFechar={() => setConferir(false)}
        titulo="Confirmar o pagamento"
        descricao="O avanço passa a esperar a conta na extranet. A etapa muda quando alguém registrar a liberação, na aba Avanços."
        rodape={<><Botao variante="fantasma" onClick={() => setConferir(false)}>Voltar</Botao><Botao carregando={acao.carregando} onClick={() => void confirmar()}>{pago ? "Confirmar" : "Confirmar o pagamento por fora"}</Botao></>}
      >
        <div className="mtc-pilha" style={{ gap: "var(--e-3)" }}>
          {acao.erro && <Aviso tipo="erro">{acao.erro}</Aviso>}
          <Fatos
            itens={[
              { rotulo: "Trader", valor: s.pessoa.nome, detalhe: s.pessoa.cpf },
              { rotulo: "Avanço", valor: ETAPA[destino] },
              ...(s.valor_centavos != null ? [{ rotulo: pago ? "Valor pago" : "Valor recebido por fora", valor: formatarReais(s.valor_centavos), mono: true }] : []),
              ...(prazo ? [{ rotulo: "Prometido ao cliente", valor: `liberação em até ${prazo}` }] : []),
              { rotulo: "Na extranet", valor: mesaReal ? "Criar a conta da Mesa Real" : `Mover a conta pra mesa de ${ETAPA[destino]}`, detalhe: "na aba Avanços, depois" },
            ]}
          />
        </div>
      </Dialogo>
      <DialogoObservacao s={s} verbo="recusar" aberto={recusar} aoFechar={() => setRecusar(false)} aoConcluir={() => depois("Avanço recusado. O cliente foi avisado com a sua observação.")} />
    </Secao>
  );
}

function Revogar({ s, aoDecidir }: { s: SolicitacaoPainel; aoDecidir: (t: string) => void }) {
  const { pode } = usePapel();
  const depois = useDepois(aoDecidir);
  const [aberto, setAberto] = useState(false);
  return (
    <Secao rotulo="Desfazer" titulo="Revogar o avanço">
      <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}>Volta o plano para {s.dados.de ? ETAPA[s.dados.de as Etapa] : "a etapa anterior"} e para a conta de antes do avanço. Desfaça o mesmo na extranet: o sistema não mexe na Nelogica.</p>
      {pode("ADMIN") ? <div><Botao variante="perigo" onClick={() => setAberto(true)}>Revogar avanço</Botao></div> : <Aviso tipo="info">Só admin pode revogar um avanço.</Aviso>}
      <DialogoObservacao s={s} verbo="revogar" aberto={aberto} aoFechar={() => setAberto(false)} aoConcluir={() => depois("Avanço revogado. O plano voltou para a etapa anterior e o cliente foi avisado.")} />
    </Secao>
  );
}

/** Recusar ou revogar: a observacao vai pro cliente, entao pede um texto de verdade. */
function DialogoObservacao({ s, verbo, aberto, aoFechar, aoConcluir }: { s: SolicitacaoPainel; verbo: "recusar" | "revogar"; aberto: boolean; aoFechar: () => void; aoConcluir: () => void }) {
  const { postar } = useApi();
  const acao = useAcao();
  const [texto, setTexto] = useState("");
  useEffect(() => { if (aberto) { setTexto(""); acao.limpar(); } }, [aberto, acao.limpar]);
  const enviar = async (ev: FormEvent) => {
    ev.preventDefault();
    const ok = await acao.rodar(() => postar(`/v1/admin/solicitacoes/${s.id}/${verbo}`, { observacao: texto.trim() }));
    if (!ok) return;
    aoFechar();
    aoConcluir();
  };
  const titulo = verbo === "recusar" ? "Recusar o pedido" : "Revogar o avanço";
  return (
    <Dialogo aberto={aberto} aoFechar={aoFechar} titulo={titulo} descricao={`${s.pessoa.nome}, ${s.matricula.plano.nome}. A observação vai para o cliente, no aviso e no e-mail.`}>
      <form className="mtc-formulario" onSubmit={enviar} noValidate>
        {acao.erro && <Aviso tipo="erro">{acao.erro}</Aviso>}
        <CampoTextoLongo rotulo="Observação para o cliente" valor={texto} aoMudar={setTexto} maxLength={1000} required placeholder={verbo === "recusar" ? "Ex.: o resultado ainda não bateu a meta de lucro." : "Ex.: o avanço foi aprovado por engano."} />
        <div className="mtc-dialogo__rodape">
          <Botao variante="fantasma" onClick={aoFechar}>Cancelar</Botao>
          <Botao type="submit" variante="perigo" carregando={acao.carregando} disabled={texto.trim().length < 3}>{verbo === "recusar" ? "Recusar" : "Revogar"}</Botao>
        </div>
      </form>
    </Dialogo>
  );
}
